import { useState, useCallback } from 'react';
import { api, DfeLegacyImportResult } from '@/lib/api';

interface UseLegacyImportReturn {
  importResult: DfeLegacyImportResult | null;
  importing: boolean;
  error: string | null;
  performImport: (server: string, username: string, password: string) => Promise<void>;
  retryImport: (importId: string, password: string) => Promise<void>;
  reset: () => void;
}

export function useLegacyImport(): UseLegacyImportReturn {
  const [importResult, setImportResult] = useState<DfeLegacyImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const performImport = useCallback(async (server: string, username: string, password: string) => {
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await api.importDfeLegacyInventory({ server, username, password });
      setImportResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }, []);

  const retryImport = useCallback(async (importId: string, password: string) => {
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await api.retryDfeLegacyImport(importId, password);
      setImportResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setImporting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setImportResult(null);
    setError(null);
    setImporting(false);
  }, []);

  return { importResult, importing, error, performImport, retryImport, reset };
}
